import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { toZonedTime, format } from 'date-fns-tz';
import { startOfMonth, getDate, addMonths, getHours } from 'date-fns';

import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import { useTranslation } from '@/config/i18n';
import { useUnivaPayTokenMethod } from '@/hooks';
import useScrollToError from '@/hooks/useScrollToError';
import { toastErrorMessage } from '@/lib/utils';
import { apiRequest } from '@/services';
import { DATE_TIME_FORMAT, HTTP_STATUS } from '@/shared/constants';
import {
  registerAccountFormSchema,
  RegisterAccountFormSchemaType,
} from '@/shared/schema/register-account';
import { GraduationTermPayload, GraduationType } from '@/types/graduation';
import { PlanType } from '@/types/plan';
import { yupResolver } from '@hookform/resolvers/yup';
import { RegisterAccountForm, RegisterAccountSuccess } from './components';

const RegisterAccountPage = () => {
  const [t] = useTranslation('');
  const { toast } = useContext(ToastContext);

  const [selectedPlan, setSelectedPlan] = useState<PlanType>({
    id: 0,
    name: '',
    price: 0,
    student_limit: 0,
    status: '',
  });
  const [graduations, setGraduations] = useState<GraduationType[]>([]);
  const [plans, setPlans] = useState<PlanType[]>([]);
  const [verifySuccess, setVerifySuccess] = useState(false);

  const { apiService } = apiRequest();
  const formMethods = useForm<RegisterAccountFormSchemaType>({
    mode: 'onChange',
    resolver: yupResolver(registerAccountFormSchema),
    shouldFocusError: false,
    defaultValues: {
      first_name_kanji: '',
      last_name_kanji: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      email: '',
      phone_number: '',
      post_code: '',
      address: '',
      graduation_term_id: undefined,
      plan_id: undefined,
    },
  });
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const { startCheckout } = useUnivaPayTokenMethod();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
  } = formMethods;

  const values = watch();

  const normalizeTimeZone = (timezone: string) => {
    if (timezone === 'Asia/Saigon') return 'Asia/Ho_Chi_Minh';
    return timezone;
  };

  const getGraduationList = useCallback(async () => {
    const { data } = await apiService.guest.collection('graduations', {
      method: 'GET',
      params: {
        timezone: normalizeTimeZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
      },
    });

    setGraduations(data?.data || []);
  }, []);

  const getPlanList = useCallback(async () => {
    const { data } = await apiService.guest.collection('plans', {
      method: 'GET',
      params: {
        timezone: normalizeTimeZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
      },
    });

    setPlans(data?.data || []);
  }, []);

  const { selectedGraduation, selectedTerm } = useMemo(() => {
    const termId = watch('graduation_term_id');
    for (const g of graduations) {
      const term = g.terms?.find((t) => t.id === termId);
      if (term) {
        return {
          selectedGraduation: g,
          selectedTerm: term,
        };
      }
    }
    return {
      selectedGraduation: {} as GraduationType,
      selectedTerm: undefined as GraduationTermPayload | undefined,
    };
  }, [graduations, values.graduation_term_id]);

  const handleClearCache = async (key: string) => {
    await apiService.coach.collection('clear', {
      method: 'POST',
      body: { key: key || '' },
    });
    localStorage.removeItem('tokenKeyPayment');
  };

  const onRegisterAccount = async (data: RegisterAccountFormSchemaType) => {
    setLoadingGlobal(true);

    const tokenKeyPayment = localStorage.getItem('tokenKeyPayment');
    if (tokenKeyPayment) {
      await handleClearCache(tokenKeyPayment);
    }

    const { data: dataResponse, error } = await apiService.coach.collection(
      'register',
      {
        method: 'POST',
        body: {
          ...data,
          date_of_birth: dayjs(data.date_of_birth.toString()).format(
            'YYYY-MM-DD',
          ),
        },
      },
    );

    if (error) {
      handleError(error);
      setTimeout(() => {
        scrollToError();
      }, 500);
      setLoadingGlobal(false);
      return;
    }

    const {
      data: { key },
    } = dataResponse;

    const timeZone = 'Asia/Tokyo';
    const now = toZonedTime(new Date(), timeZone);
    const startDate = toZonedTime(
      selectedTerm?.due_payment_date
        ? new Date(selectedTerm.due_payment_date)
        : new Date(),
      timeZone,
    );

    let subscriptionStart: string;

    if (startDate <= now) {
      const nextMonthStart = startOfMonth(addMonths(now, 1));
      subscriptionStart = format(
        nextMonthStart,
        DATE_TIME_FORMAT.DATE_TIME_HYPHEN,
        { timeZone },
      );
    } else {
      const startOfSelected = startOfMonth(startDate);
      subscriptionStart = format(
        startOfSelected,
        DATE_TIME_FORMAT.DATE_TIME_HYPHEN,
        { timeZone },
      );
    }

    if (getHours(now) < 10 && getDate(now) === 1) {
      subscriptionStart = format(
        addMonths(startOfMonth(now), 1),
        DATE_TIME_FORMAT.DATE_TIME_HYPHEN,
        { timeZone },
      );
    }

    setTimeout(() => {
      startCheckout(
        `アートシフトアプリ${selectedGraduation.period_number}期`,
        'アートシフトトレーニングアプリ',
        // eslint-disable-next-line no-irregular-whitespace
        `初回はカード登録　　　　　　　以降毎月￥${selectedPlan.price.toLocaleString()}のお支払い　　　　　　　　　　　　次回課金日：${dayjs(subscriptionStart).format(DATE_TIME_FORMAT.DATE_ONLY)}`,
        data.email,
        data.phone_number,
        async (tokenId: string) => {
          const { data, error } = await apiService.coach.collection('verify', {
            method: 'POST',
            body: {
              key,
              recurring_token_id: tokenId,
            },
          });

          if (error) {
            toast?.current?.show(toastErrorMessage(error.message));
            setLoadingGlobal(false); // Added this line
            return;
          }

          if (data) {
            setVerifySuccess(true);
          }
        },
        key,
      );
    }, 500);
    setLoadingGlobal(false);
  };

  function handleError(error: any) {
    const { status_code, errors, message } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      errors.forEach((e: any) => {
        const { field, message } = e;
        setError(field, { message });
      });
      return;
    }

    const msg = message;
    toast?.current?.show(toastErrorMessage(msg));
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  useEffect(() => {
    if (plans.length) {
      setSelectedPlan(plans[0]);
      setValue('plan_id', plans[0].id);
    }
  }, [plans]);

  useEffect(() => {
    getGraduationList();
    getPlanList();
  }, [getGraduationList, getPlanList]);

  return (
    <div className="min-h-screen bg-primary-50 pt-[64px]">
      <div className="w-full max-w-[1000px] bg-white m-auto py-8 lg:py-[64px] px-4 md:px-10 lg:px-[100px]">
        <div className="flex flex-col items-center justify-center">
          <h1 className="text-[32px] md:text-[48px] text-[#181D27] leading-[40px] md:leading-[60px] tracking-[-0.02em] text-center font-semibold whitespace-pre-line">
            {t('register_account.title')}
          </h1>
          {!verifySuccess && (
            <p className="text-gray-400 text-[16px] md:text-[20px] leading-[24px] md:leading-[30px] font-normal text-center mt-2">
              {t('register_account.sub_title')}
            </p>
          )}
        </div>

        <div className="mt-[64px]">
          {verifySuccess && <RegisterAccountSuccess />}

          {!verifySuccess && (
            <RegisterAccountForm
              formMethods={formMethods}
              onRegisterAccount={handleSubmit(onRegisterAccount)}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              loadingGlobal={loadingGlobal}
              graduations={graduations}
              plans={plans}
            />
          )}
        </div>
      </div>

      <div className="w-full py-8">
        <div className="flex flex-col items-center text-black text-sm md:text-md leading-8 font-normal px-4 text-center">
          <div className="flex flex-col md:flex-row gap-0 md:gap-2">
            <Link
              to="https://marketing-full-support.co.jp/policy"
              target="_blank"
              className="hover:text-primary-500 whitespace-nowrap"
            >
              {t('register_account.footer_section.privacy_policy')}
            </Link>
            <span className="hidden md:block text-gray-900 text-[16px] leading-[24px] font-normal">
              |
            </span>
            <Link
              to="https://marketing-full-support.co.jp/law"
              target="_blank"
              className="hover:text-primary-500 whitespace-nowrap"
            >
              {t('register_account.footer_section.commercial_transaction')}
            </Link>
          </div>
          <p className="mt-2 md:mt-0">{t('register_account.footer_section.copyright')}</p>
        </div>
      </div>
    </div>
  );
};

export default RegisterAccountPage;
